import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import { GeneratedWorkflowDto, RunRxValidationBulkDto } from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import useStore from "../../../helpers/useStore";
import { RegistryContext } from "../../../stores/Registry/registry.provider";
import { RegistryRxFilters } from "../../../stores/Registry/registryStore";

/**
 * Thin controller for the on-demand "Rezeptprüfung" (bulk Rx validation) run.
 *
 * The live workflow subscription is owned by the app-level
 * `RxValidationSocketBridge`, which streams batches + the final result straight
 * into the RegistryStore. This hook only:
 *  - starts a run (`POST /rx/rxValidationBulk/run`) for a given Standort and
 *    filter set, and records its workflow id — the bridge reacts to the new id
 *    and subscribes;
 *  - exposes the store-backed run state for the Rx tab to read.
 *
 * Because the bridge never unmounts, results keep accumulating while the user is
 * on other pages, so the tab needs no replay/re-subscribe on remount.
 */

/**
 * The registry filters the run understands. The backend derives the workflow id
 * from this payload, so what is in here decides both what gets checked AND when
 * a previous run may be reused: a filter that is applied but missing from the
 * payload would make two different questions share one answer, which is how
 * MD-184 produced "every Bearbeitungsstatus gives 192 errors".
 *
 * Built in a fixed key order so `runKey` below is a stable identity.
 */
const buildRunInput = (
  locationId: string | undefined,
  filters: RegistryRxFilters,
): RunRxValidationBulkDto => ({
  locationId,
  processingStatus: filters.processingStatus,
  doctorReportStatus: filters.doctorReportStatus,
  tarifGroupId: filters.tarifGroupId,
  rxTypeId: filters.rxTypeId,
  accountId: filters.accountId,
  prescribedTherapyId: filters.prescribedTherapyId,
  lastName: filters.lastName || undefined,
  rxNumber: filters.rxNumber || undefined,
});

/** Identity of a run's inputs — used to notice that a re-entry needs a new run. */
export const rxValidationRunKey = (
  locationId: string | undefined,
  filters: RegistryRxFilters,
): string => JSON.stringify(buildRunInput(locationId, filters));

export const useRxValidationMode = () => {
  const registryStore = useStore(RegistryContext);
  const { rx: rxApi } = useApi();

  /**
   * Start a validation run for `locationId` and the registry's current filters.
   * When `locationId` is omitted the run covers ALL locations, so callers must
   * pass the Standort the user is looking at or the results quietly span every
   * practice.
   *
   * `force` is for "Erneut prüfen": the backend replays a completed run's stored
   * result for its TTL, which is right when the question is unchanged and wrong
   * when the user is explicitly asking to re-check the data. A fresh `runId`
   * makes it a distinct workflow (and therefore a new workflow id, which is what
   * the socket bridge reacts to).
   */
  const start = useCallback(
    async ({
      locationId,
      filters,
      force = false,
    }: {
      locationId?: string;
      filters: RegistryRxFilters;
      force?: boolean;
    }) => {
      registryStore.resetValidationResults();
      registryStore.setValidationStatus("running");
      registryStore.setValidationRunKey(rxValidationRunKey(locationId, filters));
      try {
        const workflow: GeneratedWorkflowDto = await rxApi.runRxValidationBulk({
          ...buildRunInput(locationId, filters),
          ...(force ? { runId: uuidv4() } : {}),
        });
        registryStore.setValidationWorkflowId(workflow.workflowId);
      } catch {
        registryStore.setValidationStatus("error");
      }
    },
    [registryStore, rxApi],
  );

  return {
    start,
    /** Live, smoothed progress (0–100), fed by the socket bridge. */
    progress: registryStore.validationProgress,
    rows: registryStore.validationRows,
    count: registryStore.validationRows.length,
    processed: registryStore.validationProcessed,
    total: registryStore.validationTotal,
    status: registryStore.validationStatus,
    /** Inputs the currently displayed result was computed for. */
    runKey: registryStore.validationRunKey,
  };
};
